import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { FormProvider, useNotificationContext } from 'core/states';
import { useCorporatePartCostActions } from 'corporate/hooks';
import { ICorporatePart, ICorporatePartCostListItem } from 'corporate/types';

import { EndActiveCostConfirmation } from './EndActiveCostConfirmation';
import {
  getDefaultValues,
  mapActiveOverlappingCostPayload,
  mapUpsertCorporatePartCost,
} from './mappers';
import { upsertCorporatePartCostSchema } from './schema';
import { IUpsertCorporatePartCostForm } from './types';
import { UpsertCorporatePartCostFormFields } from './UpsertCorporatePartCostFormFields';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  cost?: ICorporatePartCostListItem;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  onRemoveSuccess?: () => void;
}

export const UpsertCorporatePartCost = ({
  corporatePart,
  cost,
  isOpen,
  onClose,
  onSubmitSuccess,
  onRemoveSuccess,
}: IProps) => {
  const [removedItems, setRemovedItems] = useState<number[]>([]);
  const [pendingFormData, setPendingFormData] = useState<IUpsertCorporatePartCostForm | null>(null);
  const [pendingReset, setPendingReset] =
    useState<UseFormReset<IUpsertCorporatePartCostForm> | null>(null);
  const [isEndActiveCostOpen, setIsEndActiveCostOpen] = useState(false);

  const { clearNotification } = useNotificationContext();
  const { upsertCorporatePartCost, checkActiveOverlappingCost } = useCorporatePartCostActions();

  const defaultValues = useMemo(() => getDefaultValues(corporatePart, cost), [corporatePart, cost]);

  const handleOnClose = (reset?: UseFormReset<IUpsertCorporatePartCostForm>) => {
    clearNotification();
    setRemovedItems([]);
    setPendingFormData(null);
    setPendingReset(null);
    setIsEndActiveCostOpen(false);
    onClose();
    reset?.(defaultValues);
  };

  const submitUpsert = async (
    data: IUpsertCorporatePartCostForm,
    reset: UseFormReset<IUpsertCorporatePartCostForm>
  ) => {
    const payload = mapUpsertCorporatePartCost(data, corporatePart, cost, removedItems);

    const { success } = await upsertCorporatePartCost(payload);

    if (!success) {
      return;
    }

    onSubmitSuccess?.();

    setTimeout(() => handleOnClose(reset), 1000);
  };

  const onSubmit =
    (reset: UseFormReset<IUpsertCorporatePartCostForm>) =>
    async (data: IUpsertCorporatePartCostForm) => {
      const overlapPayload = mapActiveOverlappingCostPayload(data, corporatePart, cost);
      const { record: activeCorporatePartCost } = await checkActiveOverlappingCost(overlapPayload);

      if (activeCorporatePartCost?.id) {
        setPendingFormData(data);
        setPendingReset(() => reset);
        setIsEndActiveCostOpen(true);

        return;
      }

      await submitUpsert(data, reset);
    };

  const onConfirmEndActive = async () => {
    if (!pendingFormData || !pendingReset) {
      return;
    }

    setIsEndActiveCostOpen(false);

    await submitUpsert(pendingFormData, pendingReset);
  };

  const onCancelEndActive = () => {
    setIsEndActiveCostOpen(false);
    setPendingFormData(null);
    setPendingReset(null);
  };

  const handleRemoveSuccess = () => {
    onRemoveSuccess?.();
    setTimeout(() => handleOnClose(), 1000);
  };

  return (
    <FormProvider
      schema={upsertCorporatePartCostSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertCorporatePartCostFormFields
        cost={cost}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        onItemRemoved={(itemId) => setRemovedItems((prev) => [...prev, itemId])}
        onRemoveSuccess={handleRemoveSuccess}
      />

      <EndActiveCostConfirmation
        isOpen={isEndActiveCostOpen}
        onConfirm={onConfirmEndActive}
        onCancel={onCancelEndActive}
      />
    </FormProvider>
  );
};
